import i18n from "@/i18n";
/**
 * VuosilomaEvidenceDialog — VUOSILOMA-V1.2B. Records audited factual
 * evidence used by the annual-leave month evaluation: hours the employee
 * would have worked, the contractual break arrangement, and whether two
 * sick-leave records are the same uninterrupted incapacity (no diagnosis).
 * It never chooses the legal rule; the backend decides what counts.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { formatYMDDate } from "./workforceHelpers";
import {
    buildEvidencePayload, VL_EVIDENCE_KINDS, VL_FAMILY_KIND, VL_LEGAL_BASIS, VL_PAY_BASIS,
    vlErrorMessage, vlLegalBasesFor,
} from "./vuosilomaHelpers";

function Pick({ value, onChange, options, testid, placeholder }) {
    return (
        <Select value={value} onValueChange={onChange}>
            <SelectTrigger className="mt-1" data-testid={testid}>
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
                {options.map(([v, label]) => (
                    <SelectItem key={v} value={v} data-testid={`${testid}-opt-${v}`}>{label}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

function Field({ label, children }) {
    return <label className="block"><span className="text-neutral-700">{label}</span>{children}</label>;
}

function HoursFields({ f, set }) {
    return (
        <div className="grid grid-cols-2 gap-3">
            <Field label={i18n.t("wf.vuosilomaEvidenceDialog.date", "Date")}>
                <Input type="date" className="mt-1" value={f.date || ""} data-testid="vl-ev-date"
                    onChange={(e) => set({ date: e.target.value })} />
            </Field>
            <Field label={i18n.t("wf.vuosilomaEvidenceDialog.hoursContract", "Hours (contract)")}>
                <Input type="number" min="0.25" step="0.25" className="mt-1" value={f.hours || ""}
                    data-testid="vl-ev-hours" onChange={(e) => set({ hours: e.target.value })} />
            </Field>
        </div>
    );
}

function BreakFields({ f, set }) {
    return (
        <div className="space-y-3">
            <Field label={i18n.t("wf.vuosilomaEvidenceDialog.appliesFrom", "Applies from")}>
                <Input type="date" className="mt-1" value={f.effectiveFrom || ""} data-testid="vl-ev-break-from"
                    onChange={(e) => set({ effectiveFrom: e.target.value })} />
            </Field>
            <Field label={i18n.t("wf.vuosilomaEvidenceDialog.arrangement", "Arrangement")}>
                <Pick value={f.arrangement || ""} testid="vl-ev-break-arrangement" placeholder={i18n.t("wf.vuosilomaEvidenceDialog.choose", "Choose")}
                    onChange={(v) => set({ arrangement: v })}
                    options={[["BREAK_COUNTED_AS_WORK", "Breaks are working time"],
                        ["UNPAID_BREAK", "Unpaid break deducted"]]} />
            </Field>
            {f.arrangement === "UNPAID_BREAK" && (
                <div className="grid grid-cols-2 gap-3">
                    <Field label={i18n.t("wf.vuosilomaEvidenceDialog.unpaidMinutes", "Unpaid minutes")}>
                        <Input type="number" min="1" className="mt-1" value={f.breakMinutes || ""}
                            data-testid="vl-ev-break-minutes" onChange={(e) => set({ breakMinutes: e.target.value })} />
                    </Field>
                    <Field label={i18n.t("wf.vuosilomaEvidenceDialog.onShiftsLongerThanH", "On shifts longer than (h)")}>
                        <Input type="number" min="0" step="0.5" className="mt-1" value={f.overHours || ""}
                            data-testid="vl-ev-break-over" onChange={(e) => set({ overHours: e.target.value })} />
                    </Field>
                </div>
            )}
        </div>
    );
}

function ContinuityFields({ f, set, sick }) {
    const opts = sick.map((a) => [a.absence_id,
        `${formatYMDDate(a.start_date)} – ${formatYMDDate(a.end_date)}`]);
    return (
        <div className="space-y-3">
            <Field label={i18n.t("wf.vuosilomaEvidenceDialog.earlierSickLeaveRecord", "Earlier sick-leave record")}>
                <Pick value={f.previousAbsenceId || ""} testid="vl-ev-prev-absence" placeholder={i18n.t("wf.vuosilomaEvidenceDialog.choose", "Choose")}
                    onChange={(v) => set({ previousAbsenceId: v })} options={opts} />
            </Field>
            <Field label={i18n.t("wf.vuosilomaEvidenceDialog.laterSickLeaveRecord", "Later sick-leave record")}>
                <Pick value={f.absenceId || ""} testid="vl-ev-absence" placeholder={i18n.t("wf.vuosilomaEvidenceDialog.choose", "Choose")}
                    onChange={(v) => set({ absenceId: v })} options={opts} />
            </Field>
            <Field label={i18n.t("wf.vuosilomaEvidenceDialog.fact", "Fact")}>
                <Pick value={f.continuity || ""} testid="vl-ev-continuity" placeholder={i18n.t("wf.vuosilomaEvidenceDialog.choose", "Choose")}
                    onChange={(v) => set({ continuity: v })}
                    options={[["SAME_UNINTERRUPTED_INCAPACITY", "Same uninterrupted incapacity"],
                        ["NEW_INCAPACITY", "New incapacity (employee was fit for work)"]]} />
            </Field>
        </div>
    );
}

function FamilyFields({ f, set, family }) {
    const rec = family.find((a) => a.absence_id === f.absenceId);
    const kinds = rec?.absence_type === "PARENTAL_LEAVE" ? ["PARENTAL_LEAVE"]
        : ["SPECIAL_PREGNANCY_LEAVE", "PREGNANCY_LEAVE"];
    const opts = family.map((a) => [a.absence_id, `${a.absence_type === "PARENTAL_LEAVE" ? i18n.t("wf.vuosilomaEvidenceDialog.parental", "Parental")
        : i18n.t("wf.vuosilomaEvidenceDialog.pregnancy", "Pregnancy")} leave ${formatYMDDate(a.start_date)} – ${formatYMDDate(a.end_date)}`]);
    return (
        <div className="space-y-3">
            <Field label={i18n.t("wf.vuosilomaEvidenceDialog.leaveRecord", "Leave record")}>
                <Pick value={f.absenceId || ""} testid="vl-ev-family-absence" placeholder={i18n.t("wf.vuosilomaEvidenceDialog.choose", "Choose")}
                    onChange={(v) => set({ absenceId: v, familyKind: "" })} options={opts} />
            </Field>
            <Field label={i18n.t("wf.vuosilomaEvidenceDialog.statutoryKind", "Statutory kind")}>
                <Pick value={f.familyKind || ""} testid="vl-ev-family-kind" placeholder={i18n.t("wf.vuosilomaEvidenceDialog.choose", "Choose")}
                    onChange={(v) => set({ familyKind: v })} options={kinds.map((k) => [k, VL_FAMILY_KIND[k]])} />
            </Field>
            {f.familyKind === "PARENTAL_LEAVE" && (
                <Field label={i18n.t("wf.vuosilomaEvidenceDialog.entitlement", "Entitlement")}>
                    <Pick value={f.entitlement || ""} testid="vl-ev-family-entitlement" placeholder={i18n.t("wf.vuosilomaEvidenceDialog.choose", "Choose")}
                        onChange={(v) => set({ entitlement: v })}
                        options={[["PREGNANCY_LEAVE_ENTITLED", "Parent entitled to pregnancy leave"],
                            ["OTHER_PARENT", "Other parent"]]} />
                </Field>
            )}
            {f.familyKind && f.familyKind !== "SPECIAL_PREGNANCY_LEAVE" && (<>
                <Field label={i18n.t("wf.vuosilomaEvidenceDialog.birthAdoptionReferenceNoNamesOr", "Birth / adoption reference (no names or dates of birth)")}>
                    <Input className="mt-1" value={f.eventRef || ""} data-testid="vl-ev-family-event"
                        placeholder={i18n.t("wf.vuosilomaEvidenceDialog.eGBirth1", "e.g. Birth 1")} onChange={(e) => set({ eventRef: e.target.value })} />
                </Field>
                <Field label={i18n.t("wf.vuosilomaEvidenceDialog.leaveDaysForThisBirthAdoption", "Leave days for this birth/adoption before these records (blank if unknown)")}>
                    <Input className="mt-1" inputMode="numeric" value={f.priorDays ?? ""}
                        data-testid="vl-ev-family-prior" onChange={(e) => set({ priorDays: e.target.value })} />
                </Field>
            </>)}
            <Field label={i18n.t("wf.vuosilomaEvidenceDialog.rules", "Rules")}>
                <Pick value={f.rules || "VLL_2022_34"} testid="vl-ev-family-rules"
                    onChange={(v) => set({ rules: v })}
                    options={[["VLL_2022_34", "Family leave from 1.8.2022"],
                        ["PRE_2022_34", "Earlier rules (maternity/paternity allowance)"]]} />
            </Field>
        </div>
    );
}

const BASIS_TYPES = {
    get OTHER_FAMILY_LEAVE() { return i18n.t("wf.vuosilomaEvidenceDialog.otherFamilyLeave", "Other family leave"); },
    get PAID_LEAVE() { return i18n.t("wf.vuosilomaEvidenceDialog.paidLeave", "Paid leave"); },
    get UNPAID_LEAVE() { return i18n.t("wf.vuosilomaEvidenceDialog.unpaidLeave", "Unpaid leave"); },
};

function BasisFields({ f, set, records }) {
    const rec = records.find((a) => a.absence_id === f.absenceId);
    const opts = records.map((a) => [a.absence_id,
        `${BASIS_TYPES[a.absence_type]} ${formatYMDDate(a.start_date)} – ${formatYMDDate(a.end_date)}`]);
    const needSource = ["STATUTORY_PAY_OBLIGATION", "COLLECTIVE_AGREEMENT_PAY_OBLIGATION"].includes(f.payBasis);
    const payOpts = Object.entries(VL_PAY_BASIS).filter(([k]) => !(rec?.absence_type === "PAID_LEAVE" && k === "NO_PAY"));
    return (
        <div className="space-y-3">
            <Field label={i18n.t("wf.vuosilomaEvidenceDialog.leaveRecord", "Leave record")}>
                <Pick value={f.absenceId || ""} testid="vl-ev-basis-absence" placeholder={i18n.t("wf.vuosilomaEvidenceDialog.choose", "Choose")}
                    onChange={(v) => set({ absenceId: v, legalBasis: "" })} options={opts} />
            </Field>
            <Field label={i18n.t("wf.vuosilomaEvidenceDialog.legalReasonForTheAbsence", "Legal reason for the absence")}>
                <Pick value={f.legalBasis || ""} testid="vl-ev-basis-legal" placeholder={i18n.t("wf.vuosilomaEvidenceDialog.choose", "Choose")}
                    onChange={(v) => set({ legalBasis: v })}
                    options={vlLegalBasesFor(rec?.absence_type).map((k) => [k, VL_LEGAL_BASIS[k]])} />
            </Field>
            <Field label={i18n.t("wf.vuosilomaEvidenceDialog.didTheEmployerHaveToPay", "Did the employer have to pay? (leave blank if not known)")}>
                <Pick value={f.payBasis || ""} testid="vl-ev-basis-pay" placeholder={i18n.t("wf.vuosilomaEvidenceDialog.notKnown", "Not known")}
                    onChange={(v) => set({ payBasis: v })} options={payOpts} />
            </Field>
            {needSource && (
                <Field label={i18n.t("wf.vuosilomaEvidenceDialog.whichLawOrAgreementSectionRequires", "Which law or agreement section requires the pay")}>
                    <Input className="mt-1" value={f.paySource || ""} data-testid="vl-ev-basis-pay-source"
                        placeholder={i18n.t("wf.vuosilomaEvidenceDialog.eGMaravaTes23", "e.g. MaRaVa TES 23 §")} onChange={(e) => set({ paySource: e.target.value })} />
                </Field>
            )}
            {f.legalBasis === "CAREGIVER_LEAVE" && (
                <Field label={i18n.t("wf.vuosilomaEvidenceDialog.caregiverLeaveWorkdaysAlreadyUsedThis", "Caregiver leave workdays already used this calendar year outside this record (blank if unknown)")}>
                    <Input className="mt-1" inputMode="numeric" value={f.usedElsewhere ?? ""}
                        data-testid="vl-ev-basis-used" onChange={(e) => set({ usedElsewhere: e.target.value })} />
                </Field>
            )}
            <p className="text-xs text-neutral-500" data-testid="vl-ev-basis-note">
                {i18n.t("wf.vuosilomaEvidenceDialog.basisNote", "No diagnosis or family member details are needed. Whether the leave was paid is kept separately; MiseOne decides from the legal reason and the pay obligation what counts for annual-leave earning.")}
            </p>
        </div>
    );
}

export default function VuosilomaEvidenceDialog({
    open, onOpenChange, employeeId, restaurantId, onSaved, initialKind, initialAbsenceId,
}) {
    const [kind, setKind] = useState("WOULD_HAVE_WORKED");
    const [f, setF] = useState({});
    const [sick, setSick] = useState([]);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState("");
    const set = (patch) => setF((x) => ({ ...x, ...patch }));

    useEffect(() => {
        if (!open) return;
        setKind(initialKind || "WOULD_HAVE_WORKED");
        setF(initialAbsenceId ? { absenceId: initialAbsenceId } : {});
        setError("");
        api.get("/workforce/absences", { params: { restaurant_id: restaurantId, employee_id: employeeId } })
            .then(({ data }) => setSick(data?.absences || []))
            .catch(() => setSick([]));
    }, [open, employeeId, restaurantId, initialKind, initialAbsenceId]);

    const canSave = !pending && (f.reason || "").trim().length >= 3 && (f.source || "").trim().length >= 3;
    const save = async () => {
        setPending(true);
        setError("");
        try {
            await api.post(`/workforce/employees/${employeeId}/vuosiloma/evidence`, buildEvidencePayload(kind, f));
            toast.success(i18n.t("wf.vuosilomaEvidenceDialog.evidenceSaved", "Evidence saved"));
            onOpenChange(false);
            onSaved?.();
        } catch (err) {
            setError(vlErrorMessage(err));
        } finally {
            setPending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="vl-evidence-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{i18n.t("wf.vuosilomaEvidenceDialog.addAnnualLeaveEvidence", "Add annual-leave evidence")}</DialogTitle>
                    <DialogDescription>
                        {i18n.t("wf.vuosilomaEvidenceDialog.recordTheActualContractArrangementAnd", "Record the actual contract arrangement and where it comes from. MiseOne applies the statutory limits itself; this does not decide what counts.")}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 text-sm">
                    <Field label={i18n.t("wf.vuosilomaEvidenceDialog.evidenceType", "Evidence type")}>
                        <Pick value={kind} testid="vl-ev-kind" onChange={(v) => { setKind(v); setError(""); }}
                            options={Object.entries(VL_EVIDENCE_KINDS)} />
                    </Field>
                    {kind === "WOULD_HAVE_WORKED" && <HoursFields f={f} set={set} />}
                    {kind === "BREAK_TREATMENT" && <BreakFields f={f} set={set} />}
                    {kind === "SICK_CONTINUITY" && (
                        <ContinuityFields f={f} set={set} sick={sick.filter((a) => a.absence_type === "SICK_LEAVE")} />
                    )}
                    {kind === "FAMILY_LEAVE_CLASSIFICATION" && (
                        <FamilyFields f={f} set={set} family={sick.filter((a) =>
                            ["PREGNANCY_LEAVE", "PARENTAL_LEAVE"].includes(a.absence_type))} />
                    )}
                    {kind === "ABSENCE_BASIS_CLASSIFICATION" && (
                        <BasisFields f={f} set={set} records={sick.filter((a) => a.absence_type in BASIS_TYPES)} />
                    )}
                    <Field label={i18n.t("wf.vuosilomaEvidenceDialog.sourceContractAgreementRoster", "Source (contract, agreement, roster)")}>
                        <Input className="mt-1" value={f.source || ""} data-testid="vl-ev-source"
                            placeholder={i18n.t("wf.vuosilomaEvidenceDialog.eGEmploymentContract3", "e.g. Employment contract §3")} onChange={(e) => set({ source: e.target.value })} />
                    </Field>
                    <Field label={i18n.t("wf.vuosilomaEvidenceDialog.reason", "Reason")}>
                        <Textarea rows={2} className="mt-1" value={f.reason || ""} data-testid="vl-ev-reason"
                            onChange={(e) => set({ reason: e.target.value })} />
                    </Field>
                    {error && (
                        <div role="alert" data-testid="vl-ev-error"
                            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
                            {error}
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="vl-ev-cancel">{i18n.t("common.cancel", "Cancel")}</Button>
                    <Button onClick={save} disabled={!canSave} data-testid="vl-ev-save">
                        {pending ? i18n.t("common.saving", "Saving…") : i18n.t("common.save", "Save")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
